// src/utils/teralehtiGenerator.js
// Letters too rare to form usable puzzles (c q w x z å) are excluded.
export const ALPHABET = 'abdefghijklmnoprstuvyäö';
export const MIN_WORDS = 20;
export const MAX_WORDS = 60;
export const MIN_LENGTH = 4;
export const PANGRAM_BONUS = 7;
export const FREE_CAP_FRACTION = 0.15;

// Slurs never allowed in a puzzle, not even as bonus words. Exact matches since e.g. "kyyryssä" contains "ryssä".
export const BLOCKED_WORDS = new Set([
  'neekeri', 'neekeriorja', 'neekeriorjuus', 'nekru', 'mutakuono', 'mongoloidi',
  'ryssä', 'kotiryssä', 'laukkuryssä', 'pikkuryssä', 'ryssäviha', 'hintti', 'hinttiys',
]);

// Where a candidate word goes: in the puzzle (main/bonus), shown as an Asiasanasto suggestion, or banned for good.
export const TIERS = ['main', 'bonus', 'suggest', 'banned'];
export const DEFAULT_THRESHOLDS = { kotusMin: 0.01, asiaMin: 0.1 };

const WORD_REGEX = new RegExp(`^[${ALPHABET}]{${MIN_LENGTH},}$`);
const MAX_ATTEMPTS = 2000;

const bit = (c) => 1 << ALPHABET.indexOf(c);
const maskOf = (word) => [...word].reduce((m, c) => m | bit(c), 0);
const popcount = (m) => { let n = 0; while (m) { m &= m - 1; n++; } return n; };
const lettersOf = (mask) => [...ALPHABET].filter(c => mask & bit(c)).join('');

// public/teralehti_words.tsv (scripts/buildTeralehtiWords.js): word, source k|a, frequency per million ("~" = estimate), flags.
const parseLine = (line) => {
  const [word, src, freq = '', flags = ''] = line.split('\t');
  return {
    word,
    src,
    freq: freq ? Number(freq.replace('~', '')) : null,
    estimated: freq.startsWith('~'),
    flags: flags ? flags.split(',') : [],
    mask: maskOf(word),
  };
};

let dictionaryPromise = null;

export const loadDictionary = () => {
  if (!dictionaryPromise) {
    dictionaryPromise = fetch('/teralehti_words.tsv')
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then(text => {
        const words = text.split('\n').filter(Boolean).map(parseLine)
          .filter(w => WORD_REGEX.test(w.word) && !BLOCKED_WORDS.has(w.word));
        const byWord = new Map(words.map(w => [w.word, w]));
        // Pangrams must be main words, so only Kotus words seed puzzles.
        const seeds = [...new Set(words.filter(w => w.src === 'k' && popcount(w.mask) === 7).map(w => w.mask))];
        return { words, byWord, seeds };
      })
      .catch(e => {
        dictionaryPromise = null;
        throw e;
      });
  }
  return dictionaryPromise;
};

export const scoreWord = (word, isPangram) =>
  (word.length === MIN_LENGTH ? 1 : word.length) + (isPangram ? PANGRAM_BONUS : 0);

export const isPangram = (word, letters) => new Set(word).size === 7 && [...letters].every(c => word.includes(c));

export const puzzleStats = (words, letters) => {
  const pangrams = words.filter(w => isPangram(w, letters));
  const maxScore = words.reduce((s, w) => s + scoreWord(w, isPangram(w, letters)), 0);
  return { count: words.length, pangrams, maxScore, freeCapScore: Math.max(1, Math.round(maxScore * FREE_CAP_FRACTION)) };
};

// Returns dictionary entries ({ word, src, freq, estimated, flags }).
export const buildCandidates = (dict, letters, center) => {
  const setMask = maskOf(letters);
  const centerBit = bit(center);
  return dict.words.filter(w => (w.mask & ~setMask) === 0 && (w.mask & centerBit));
};

// Tier for a word the admin hasn't placed in this puzzle. Explicit decisions (lists = { banned, main, removed })
// win; otherwise rare Kotus words start as bonus and only common, unflagged Asiasanasto words
// get in (as bonus), the rest waiting in suggestions.
export const defaultTier = (entry, lists, { kotusMin, asiaMin } = DEFAULT_THRESHOLDS) => {
  if (lists.banned.has(entry.word)) return 'banned';
  if (lists.main.has(entry.word)) return 'main';
  if (lists.removed.has(entry.word)) return 'bonus';
  if (entry.src === 'k') return (entry.freq ?? 0) < kotusMin ? 'bonus' : 'main';
  return entry.flags.length === 0 && !entry.estimated && entry.freq >= asiaMin ? 'bonus' : 'suggest';
};

export const isValidLetters = (letters, center) =>
  letters.length === 7 &&
  new Set(letters).size === 7 &&
  [...letters].every(c => ALPHABET.includes(c)) &&
  letters.includes(center);

// Center is ignored: the same letters with another center share the pangrams and most words, so it would feel like a repeat.
export const setKey = (letters) => [...letters].sort().join('');

// A word the puzzle must contain: it has to be in the dictionary and use at most 7 distinct letters.
// Returns { entry } or { error }.
export const checkMustWord = (dict, raw) => {
  const word = raw.trim().toLowerCase();
  if (![...word].every(c => ALPHABET.includes(c))) return { error: 'Sana saa sisältää vain kirjaimia a–ö (ei c q w x z å)' };
  if (new Set(word).size > 7) return { error: 'Sanassa saa olla enintään 7 eri kirjainta' };
  const entry = dict.byWord.get(word);
  if (!entry) return { error: `Sanaa "${word}" ei löydy sanastosta (vähintään ${MIN_LENGTH} kirjainta)` };
  return { entry };
};

// Picks a random seed pangram set and center until the main word count lands in range.
// mustContain (a dictionary entry) limits sets to those containing all its letters, centered on one of them.
export const generatePuzzle = (dict, { usedSetKeys = new Set(), tierOf = () => 'main', mustContain = null } = {}) => {
  let fallback = null;
  const seeds = mustContain ? dict.seeds.filter(s => (s & mustContain.mask) === mustContain.mask) : dict.seeds;
  if (seeds.length === 0) return null;
  const centerChoices = mustContain ? [...new Set(mustContain.word)] : null;
  for (let i = 0; i < MAX_ATTEMPTS; i++) {
    const seed = seeds[Math.floor(Math.random() * seeds.length)];
    const letters = lettersOf(seed);
    const center = centerChoices
      ? centerChoices[Math.floor(Math.random() * centerChoices.length)]
      : letters[Math.floor(Math.random() * 7)];
    if (usedSetKeys.has(setKey(letters))) continue;

    const candidates = buildCandidates(dict, letters, center);
    if (mustContain && !['main', 'bonus'].includes(tierOf(mustContain))) continue;
    const main = candidates.filter(w => tierOf(w) === 'main');
    if (!main.some(w => isPangram(w.word, letters))) continue;
    if (main.length < MIN_WORDS || main.length > MAX_WORDS) continue;

    // Prefer sets where few Kotus words got pushed out of the main list.
    const pushedOut = candidates.filter(w => w.src === 'k' && tierOf(w) !== 'main').length;
    const result = { letters, center, candidates };
    if (pushedOut <= main.length * 0.25) return result;
    fallback = fallback || result;
  }
  return fallback;
};
